import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, RefreshCw, TrendingUp, Clock, Lightbulb, Calendar, ShieldAlert, AlertTriangle, Eye, Euro } from "lucide-react";

// Minimal markdown renderer for the AI text (h2 + bullet lists + bold + paragraphs)
const renderMarkdown = (text) => {
    if (!text) return null;
    const lines = text.split("\n");
    const out = [];
    let listBuffer = [];

    const flushList = (key) => {
        if (listBuffer.length) {
            out.push(<ul key={`ul-${key}`}>{listBuffer.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(it) }} />)}</ul>);
            listBuffer = [];
        }
    };
    const inline = (s) => s
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/`([^`]+)`/g, "<code>$1</code>");

    lines.forEach((raw, i) => {
        const l = raw.trimEnd();
        if (l.startsWith("## ")) {
            flushList(i);
            out.push(<h2 key={`h-${i}`}>{l.replace(/^##\s*/, "")}</h2>);
        } else if (/^[-*]\s+/.test(l)) {
            listBuffer.push(l.replace(/^[-*]\s+/, ""));
        } else if (l.trim() === "") {
            flushList(i);
        } else {
            flushList(i);
            out.push(<p key={`p-${i}`} dangerouslySetInnerHTML={{ __html: inline(l) }} />);
        }
    });
    flushList("end");
    return out;
};

const InsightsAI = () => {
    const [history, setHistory] = useState([]);
    const [active, setActive] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [voidAlerts, setVoidAlerts] = useState(null);

    const loadHistory = async () => {
        try {
            const { data } = await api.get("/insights/history");
            setHistory(data);
            if (data.length && !active) setActive(data[0]);
        } catch (_e) { /* */ }
    };

    const loadVoidAlerts = async () => {
        try {
            const { data } = await api.get("/insights/voided-alerts");
            setVoidAlerts(data);
        } catch (_e) { /* silent */ }
    };

    useEffect(() => {
        loadHistory();
        loadVoidAlerts();
        // Refresh anomalie ogni 5 min
        const t = setInterval(loadVoidAlerts, 5 * 60 * 1000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, []);

    const generate = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post("/insights/ai");
            setActive(data);
            await loadHistory();
            toast.success(
                data.consumed_from === "free"
                    ? "Insights generati (richiesta gratuita usata)"
                    : "Insights generati (richiesta dal pacchetto acquistato)"
            );
        } catch (e) {
            const status = e?.response?.status;
            if (status === 402) {
                toast.error("Quota AI esaurita", { description: "Acquista un pacchetto da 50 richieste a €10 dalla pagina Abbonamento." });
            } else {
                toast.error(formatError(e));
            }
        }
        setGenerating(false);
    };

    return (
        <div className="space-y-6" data-testid="insights-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2 flex items-center gap-2">
                        <Sparkles className="w-3 h-3" /> AI Powered
                    </div>
                    <h1 className="font-display text-4xl font-bold text-warm">Insights AI Sala</h1>
                    <p className="text-elegant mt-2 max-w-2xl">
                        Analisi intelligente del servizio: top piatti, tempi di preparazione, bottleneck cucina e
                        raccomandazioni operative generate automaticamente da Claude Sonnet 4.5.
                    </p>
                </div>
                <button
                    onClick={generate}
                    disabled={generating}
                    data-testid="generate-insights-button"
                    className="h-14 px-6 bg-brand hover:bg-brand-light text-deep font-display font-bold uppercase tracking-wider rounded-md flex items-center gap-2 transition-colors disabled:opacity-60"
                >
                    {generating ? (
                        <><RefreshCw className="w-5 h-5 animate-spin" /> Analizzo i dati...</>
                    ) : (
                        <><Sparkles className="w-5 h-5" /> Genera Insights</>
                    )}
                </button>
            </div>

            {/* Alert anomalie tavoli liberati */}
            {voidAlerts?.has_alerts && (
                <div className="bg-red-950/30 border-2 border-red-600/60 rounded-lg p-4" data-testid="void-alerts">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <ShieldAlert className="w-5 h-5 text-red-300 animate-pulse" />
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-red-300">Audit automatico</div>
                                <div className="font-display text-lg font-bold text-red-100">
                                    {voidAlerts.alerts.length} {voidAlerts.alerts.length === 1 ? "anomalia rilevata" : "anomalie rilevate"} oggi
                                </div>
                            </div>
                        </div>
                        <a href="/statistiche" className="text-[10px] text-red-300 hover:text-red-100 uppercase tracking-wider flex items-center gap-1" data-testid="void-alerts-link">
                            <Eye className="w-3 h-3" /> Vedi dettagli
                        </a>
                    </div>
                    <div className="space-y-2">
                        {voidAlerts.alerts.map((a, idx) => {
                            const severityColor = a.severity === "high"
                                ? "border-red-500 bg-red-900/30 text-red-100"
                                : "border-amber-500/60 bg-amber-900/20 text-amber-100";
                            const Icon = a.type === "operator_anomaly" ? AlertTriangle :
                                          a.type === "amount_spike" ? Euro :
                                          AlertTriangle;
                            return (
                                <div key={idx}
                                    className={`flex items-start gap-3 p-3 rounded border ${severityColor}`}
                                    data-testid={`void-alert-${a.type}`}
                                >
                                    <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1 text-sm">
                                        <div className="font-bold">🚩 {a.message}</div>
                                        {a.type === "operator_anomaly" && (
                                            <div className="text-[11px] opacity-90 mt-1">
                                                Ruolo: <strong>{a.role}</strong> · Importo annullato oggi: <strong>€ {a.amount_today.toFixed(2)}</strong>
                                            </div>
                                        )}
                                        {a.type === "amount_spike" && (
                                            <div className="text-[11px] opacity-90 mt-1">
                                                Confronto con media degli ultimi 7 giorni: {a.avg_amount_last_7d > 0 ? `+${Math.round(((a.amount_today / a.avg_amount_last_7d) - 1) * 100)}%` : "primo giorno con eventi"}
                                            </div>
                                        )}
                                        {a.type === "repeated_reason" && (
                                            <div className="text-[11px] opacity-90 mt-1">
                                                Stesso testo ripetuto identicamente — verificare che non sia copia-incolla automatico
                                            </div>
                                        )}
                                    </div>
                                    {a.severity === "high" && (
                                        <span className="text-[9px] uppercase font-bold bg-red-700 text-white px-1.5 py-0.5 rounded">CRITICO</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {generating && (
                <div className="bg-surface border border-brand/30 rounded-lg p-6 shimmer">
                    <div className="text-sm text-elegant">Sto analizzando ordini, tempi di preparazione, prenotazioni, metodi di pagamento e top piatti del giorno...</div>
                </div>
            )}

            {!active && !generating && (
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-12 text-center">
                    <Sparkles className="w-12 h-12 text-brand mx-auto mb-4 opacity-60" />
                    <div className="font-display text-xl font-bold text-warm mb-2">Nessun insight ancora generato</div>
                    <div className="text-elegant text-sm max-w-md mx-auto">
                        Clicca <strong className="text-brand">Genera Insights</strong> per ricevere un'analisi
                        AI in tempo reale del servizio: trend, opportunità e azioni consigliate.
                    </div>
                </div>
            )}

            {active && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* AI Report */}
                    <div className="lg:col-span-2 bg-surface border border-[#1f2c44] rounded-lg p-6 lg:p-8" data-testid="active-insight">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-brand">Report del</div>
                                <div className="font-display text-2xl font-bold text-warm">
                                    {new Date(active.created_at).toLocaleString("it-IT")}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-xs uppercase tracking-wider bg-brand-soft text-brand px-3 py-1.5 rounded-full">
                                <Sparkles className="w-3 h-3" /> Claude Sonnet 4.5
                            </div>
                        </div>
                        <div className="prose-ai text-warm">
                            {renderMarkdown(active.ai_text)}
                        </div>
                    </div>

                    {/* Sidebar: snapshot data + history */}
                    <div className="space-y-4">
                        <div className="bg-surface border border-[#1f2c44] rounded-lg p-5">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-3">Dati Analizzati</div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-elegant flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> Incasso</span><span className="font-display font-bold text-brand">€ {active.data_snapshot?.incasso_oggi_eur?.toFixed(2)}</span></div>
                                <div className="flex justify-between"><span className="text-elegant">Documenti</span><span>{active.data_snapshot?.documenti_emessi}</span></div>
                                <div className="flex justify-between"><span className="text-elegant">Ordini</span><span>{active.data_snapshot?.ordini_oggi}</span></div>
                                <div className="flex justify-between"><span className="text-elegant">Tavoli</span><span>{active.data_snapshot?.tavoli_occupati}</span></div>
                                <div className="flex justify-between"><span className="text-elegant flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Prenotazioni</span><span>{active.data_snapshot?.prenotazioni_oggi}</span></div>
                            </div>
                            {active.data_snapshot?.tempi_preparazione_medi_min && Object.keys(active.data_snapshot.tempi_preparazione_medi_min).length > 0 && (
                                <>
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-brand mt-5 mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Tempi prep</div>
                                    <div className="space-y-1 text-xs max-h-40 overflow-y-auto">
                                        {Object.entries(active.data_snapshot.tempi_preparazione_medi_min).map(([n, m]) => (
                                            <div key={n} className="flex justify-between">
                                                <span className="text-elegant truncate pr-2">{n}</span>
                                                <span className={m < 5 ? "text-green-400" : m < 12 ? "text-amber-400" : "text-red-400"}>{m} min</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {history.length > 0 && (
                            <div className="bg-surface border border-[#1f2c44] rounded-lg p-5">
                                <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-3 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Cronologia</div>
                                <div className="space-y-1">
                                    {history.map((h) => (
                                        <button
                                            key={h.id}
                                            onClick={() => setActive(h)}
                                            data-testid={`history-${h.id}`}
                                            className={`w-full text-left p-2 rounded text-xs transition-colors ${
                                                active.id === h.id ? "bg-brand-soft text-brand" : "text-elegant hover:bg-[#0e1626]"
                                            }`}
                                        >
                                            <div className="font-medium">{new Date(h.created_at).toLocaleDateString("it-IT")}</div>
                                            <div className="text-[10px] opacity-80">{new Date(h.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} · €{h.data_snapshot?.incasso_oggi_eur?.toFixed(2)}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default InsightsAI;
